<template>
  <div class="contain">
    <!-- 검색 영역 -->
    <div class="search_form" ref="search_form">
      <el-form
        :inline="true"
        :label-position="$device.mobile ? `top` : `left`"
        label-suffix=":"
        :size="size"
        @submit.native.prevent>
        <el-row>
          <el-col :md="8">
            <el-form-item :label="$t('대상 매장')">
              <span class="dm-scope">{{ scopeStoreName(storeCd) }}</span>
            </el-form-item>
          </el-col>
          <el-col :md="8">
            <el-form-item :label="$t('형제 매장 포함')">
              <el-switch v-model="group" @change="search"></el-switch>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <!--
      **일괄 확정이 여기 있는 이유** (§16.183).

      요기요·쿠팡은 매칭에 실패한 플랫폼 메뉴로 오더엔 메뉴 행을 만들지 않으므로(§16.172), 메뉴가
      비어 있는 매장에 처음 가져오면 관측 **전량**이 이 화면에 온다 — 2026-08-06 샵인샵 실측에서
      한 샵이 38건이었다. 행마다 누르게 하면 사장님에게 준 안내("여기서 확정하세요")가 실질적으로
      답이 아니게 된다. `메뉴 연결`(bind)은 링크마다 다른 메뉴를 골라야 해서 일괄이 성립하지
      않지만, `새 메뉴로 등록`(materialize)은 관측 그대로 만드는 것이라 한 번에 된다.
    -->
    <div class="buttons" ref="buttons">
      <el-button
        :size="size"
        @click="$router.push({ path: '/v/business/delivery-menu/option-mappings', query: { storeCd } })"
        >{{ $t('옵션 연결') }}</el-button
      >
      <el-button :size="size" @click="search" icon="el-icon-search">{{ $t('검색') }}</el-button>
      <el-button
        :size="size"
        type="primary"
        :disabled="!canBind || selection.length === 0"
        :loading="bulkLoading"
        @click="openBulkMaterialize">
        {{
          selection.length > 0
            ? `${$t('선택')} ${selection.length}${$t('건 새 메뉴로 등록')}`
            : $t('선택 새 메뉴로 등록')
        }}
      </el-button>
    </div>

    <!--
      **이 목록에 두 갈래가 있다** (§16.257). 종전 문구는 「배달앱에만 있고…」 하나였는데, 이제
      `연결 확인 필요` 행(이미 오더엔 메뉴를 가리키지만 확정을 기다리는 링크)이 함께 온다 —
      그 행에 대해 옛 문구는 거짓이다.
    -->
    <el-alert
      class="dm-hint"
      type="info"
      :closable="false"
      show-icon
      :title="$t('연결을 확정해야 하는 배달앱 메뉴입니다. 확정해야 이 메뉴의 변경이 배달앱에 반영됩니다.')">
    </el-alert>

    <!-- 리스트 -->
    <el-table
      :height="tableHeight"
      ref="table"
      border
      stripe
      highlight-current-row
      tooltip-effect="dark"
      :data="tableData"
      :size="size"
      @selection-change="onSelectionChange"
      :empty-text="$t('연결을 확정해야 하는 배달앱 메뉴가 없습니다.')">
      <!--
        **`연결 확인 필요` 행은 담기지 않는다** (§16.257). 일괄은 `새 메뉴로 등록` 하나뿐인데 그
        행들은 이미 오더엔 메뉴를 가리켜 서버가 409 로 거절한다 — 담을 수 있게 두면 사장님이
        전체 선택을 누른 뒤 결과 대화상자에서야 안다. 술어는 단건 버튼과 **같은 것**을 쓴다.
      -->
      <el-table-column type="selection" width="45" align="center" :selectable="canMaterialize"></el-table-column>
      <el-table-column
        :label="tableData.length.toString()"
        type="index"
        width="55"
        align="center"
        label-class-name="data_cnt"></el-table-column>
      <el-table-column :label="$t('플랫폼')" width="100">
        <template slot-scope="s">{{ platformName(s.row.platform) }}</template>
      </el-table-column>
      <el-table-column :label="$t('매장')" width="130" show-overflow-tooltip>
        <template slot-scope="s">{{ s.row.storeNm || scopeStoreName(s.row.storeCd) }}</template>
      </el-table-column>
      <!--
        **`가게` 는 `이름(번호)` 한 줄이다** (§16.179).

        이 열이 `가게 1075202` 로 뜨던 자리다. 번호를 툴팁으로 숨기지 않는 것은 그것이 문의·진단에서
        실제로 주고받는 값이기 때문이고, 이름을 아직 관측하지 못한 링크(옛 링크는 전부 그렇다)는
        번호만 뜬다 — 그 갈래까지 `shopText` 한 곳이 답한다.
      -->
      <el-table-column :label="$t('가게')" width="180" show-overflow-tooltip>
        <template slot-scope="s">{{ shop(s.row) }}</template>
      </el-table-column>
      <el-table-column :label="$t('배달앱 메뉴명')" min-width="200" show-overflow-tooltip>
        <template slot-scope="s">
          <span v-if="s.row.observedName">{{ s.row.observedName }}</span>
          <span v-else class="dm-weak">
            {{ s.row.menuNameKey }}
            <el-tag size="mini" type="info">{{ $t('정규화 키') }}</el-tag>
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('배달앱 가격')" width="120" align="right">
        <template slot-scope="s">{{ priceText(s.row.observedPrice) }}</template>
      </el-table-column>
      <!--
        **카테고리를 보여 주는 것이 `새 메뉴로 등록`의 전제다** (§16.178).

        링크가 이름을 갖지 못했던 동안 이 버튼은 dev 실데이터에서 ORPHAN 145건 중 143건이
        "카테고리를 모른다" 400 이었다. 값이 없는 행은 그 사실을 먼저 드러내야 사장님이 누르기 전에
        무엇을 정해야 하는지 안다.
      -->
      <el-table-column :label="$t('배달앱 카테고리')" width="140" show-overflow-tooltip>
        <template slot-scope="s">
          <span v-if="s.row.observedCategoryNm">{{ s.row.observedCategoryNm }}</span>
          <span v-else class="dm-weak">{{ $t('읽지 못함 — 등록 시 지정') }}</span>
        </template>
      </el-table-column>
      <!--
        **옵션도 누르기 전에 말한다** (§16.195).

        `새 메뉴로 등록`은 배달앱 원본의 옵션그룹·옵션까지 함께 만든다. 관측하지 못한 행은 옵션 없는
        메뉴가 되므로 — 요기요·쿠팡 룰이 아직 메뉴별 옵션을 싣지 않는다 — 그 사실이 목록에서 먼저
        보여야 사장님이 등록 후에야 알게 되지 않는다(이 CS 가 그렇게 시작했다).
      -->
      <el-table-column :label="$t('배달앱 옵션')" width="110" align="right">
        <template slot-scope="s">
          <span :class="{ 'dm-weak': !hasOptions(s.row) }">{{ optionText(s.row.observedOptionCount) }}</span>
        </template>
      </el-table-column>
      <!--
        ★★ **두 갈래를 눈으로 가르는 열** (§16.257).

        값이 있으면 「이 메뉴를 가리키고 있는데 확정이 안 됐다」이고, 조치는 `메뉴 연결`을 눌러
        **같은 메뉴를 다시 확정**하는 것이다(후보 맨 앞에 그 메뉴가 온다). 비어 있으면 종전의
        `ORPHAN` — 붙일 상대를 아직 고르지 않은 행이다.
      -->
      <el-table-column :label="$t('현재 연결')" width="180" show-overflow-tooltip>
        <template slot-scope="s">
          <span v-if="s.row.smenuCd == null" class="dm-weak">{{ $t('없음') }}</span>
          <span v-else>
            {{ s.row.smenuNm || `#${s.row.smenuCd}` }}
            <el-tag size="mini" type="warning">{{ $t('확정 필요') }}</el-tag>
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('연결 후보')" min-width="220">
        <template slot-scope="s">
          <el-tag v-for="c in s.row.candidates" :key="c.smenuCd" size="mini" class="dm-candidate">
            {{ c.smenuNm }}
          </el-tag>
          <span v-if="!s.row.candidates || s.row.candidates.length === 0" class="dm-weak">
            {{ $t('후보 없음 — 직접 검색해 선택') }}
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('관측 시각')" width="150">
        <template slot-scope="s">{{ formatDate(s.row.observedAt) }}</template>
      </el-table-column>
      <!--
        **두 버튼이 항상 함께 있다** (§16.172).

        후보 유무로 버튼을 감추지 않는다 — 후보가 있어도 "이건 다른 메뉴다" 일 수 있고(그때 필요한
        것은 `새 메뉴로 등록`이다), 후보가 0건이어도 `bind` 는 직접 검색으로 붙일 수 있다(가이드 §4
        "후보가 0건 | 두 갈래다"). 조건부로 감추면 사장님이 행마다 다른 규칙을 학습해야 하고, 그
        규칙의 근거가 서버의 느슨한 후보 산출이 되어 버린다.
      -->
      <el-table-column :label="$t('조치')" width="190" align="center">
        <template slot-scope="s">
          <el-button type="text" size="mini" :disabled="!canBind" @click="openBind(s.row)">
            {{ $t('메뉴 연결') }}
          </el-button>
          <el-divider direction="vertical"></el-divider>
          <!--
            **두 버튼이 항상 함께 있다는 규칙의 유일한 예외** (§16.257) — 이 행은 이미 오더엔
            메뉴를 가리키므로 창설이 성립하지 않는다(서버 409). 감추지 않고 **막고 이유를
            말한다** — 감추면 사장님이 행마다 다른 규칙을 학습해야 한다.
          -->
          <el-tooltip
            :disabled="canMaterialize(s.row)"
            :content="$t('이미 오더엔 메뉴를 가리키는 링크입니다. 「메뉴 연결」로 확정해 주세요.')"
            placement="top">
            <span>
              <el-button
                type="text"
                size="mini"
                :disabled="!canBind || !canMaterialize(s.row)"
                @click="openMaterialize(s.row)">
                {{ $t('새 메뉴로 등록') }}
              </el-button>
            </span>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <delivery-menu-bind-dialog :visible.sync="bindVisible" :link="bindLink" :store-cd="bindStoreCd" @bound="search" />
    <delivery-menu-materialize-dialog
      :visible.sync="materializeVisible"
      :link="materializeLink"
      :categories="categories"
      :category-pickable="materializeCategoryPickable"
      @created="onMaterialized"
      @refresh="search" />

    <!--
      **일괄에도 단건과 같은 카테고리 자리를 준다** (§16.186).

      예전에는 카테고리를 못 읽은 건이 있을 때 `$prompt` 한 줄로 이름만 받았다 — 사장님이 이미
      가진 카테고리에 넣고 싶어도 이름을 그대로 다시 타이핑해야 했고, 오타 하나면 같은 뜻의
      카테고리가 하나 더 생겼다. 여러 카테고리에 넣는 것은 아예 할 수 없었다.
    -->
    <el-dialog :title="$t('메뉴를 넣을 카테고리')" :visible.sync="bulkCategoryVisible" width="560px">
      <el-alert
        class="dm-created__alert"
        type="info"
        :closable="false"
        show-icon
        :title="bulkCategoryPrompt()"></el-alert>

      <!--
        **단건과 같은 게이트다** — 목록은 세션 매장 카테고리뿐이라 형제 매장 링크가 섞여 있으면
        고르게 해서는 안 된다(서버가 링크마다 400 을 내 그 요청이 전량 실패한다). 술어는
        utils 의 `canPickStoreCategories` 한 곳이 소유한다.
      -->
      <template v-if="categories.length > 0 && bulkCategoryPickable">
        <div class="dm-bulk__sublabel">{{ $t('기존 카테고리에서 선택 (여러 개 가능)') }}</div>
        <el-checkbox-group v-model="bulkCategoryCds" class="dm-bulk__checks">
          <el-checkbox
            v-for="category in categories"
            :key="category.categoryCd"
            :label="category.categoryCd"
            border
            size="small">
            {{ category.categoryNm }}
          </el-checkbox>
        </el-checkbox-group>
      </template>

      <div class="dm-bulk__sublabel">{{ $t('새 카테고리 이름 (선택)') }}</div>
      <el-input
        v-model="bulkCategoryNm"
        size="small"
        clearable
        maxlength="50"
        show-word-limit
        :placeholder="$t('카테고리 이름 — 같은 이름이 있으면 그 카테고리를 씁니다')"></el-input>

      <div v-if="!bulkCategoryPickable" class="dm-bulk__sublabel">
        {{ $t('형제 매장의 카테고리 목록은 이 화면에서 읽을 수 없어 이름으로만 정할 수 있습니다.') }}
      </div>

      <div slot="footer">
        <el-button size="small" @click="bulkCategoryVisible = false">{{ $t('취소') }}</el-button>
        <el-button
          size="small"
          type="primary"
          :disabled="bulkCategoryCds.length === 0 && !bulkCategoryNm.trim()"
          @click="confirmBulkCategory">
          {{ $t('확인') }}
        </el-button>
      </div>
    </el-dialog>

    <!--
      **이 응답이 유일한 확인 지점이다** (가이드 §4). 목록을 다시 읽으면 그 행은 사라져 있으므로
      무엇이 만들어졌는지 여기서 말하지 않으면 어디에서도 말할 자리가 없다.
    -->
    <el-dialog :title="$t('새 메뉴로 등록했습니다')" :visible.sync="createdVisible" width="560px">
      <div v-if="created">
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item :label="$t('메뉴명')">{{ created.smenuNm || '-' }}</el-descriptions-item>
          <!-- 여러 곳에 넣을 수 있게 된 뒤로 대표 하나만 말하면 나머지를 확인할 자리가 없다 (§16.186) -->
          <el-descriptions-item :label="$t('카테고리')">{{ createdCategoryText(created) }}</el-descriptions-item>
          <el-descriptions-item :label="$t('메뉴 번호')">{{ created.smenuCd }}</el-descriptions-item>
          <!-- 무엇이 함께 만들어졌는지도 여기서만 말할 수 있다 (§16.195) -->
          <el-descriptions-item :label="$t('옵션')">{{ createdOptionText(created) }}</el-descriptions-item>
        </el-descriptions>
        <el-alert
          v-if="createdPriceUnknown"
          class="dm-hint dm-created__alert"
          type="warning"
          :closable="false"
          show-icon
          :title="$t('배달앱 가격을 읽지 못해 0원으로 만들어졌습니다. 메뉴 수정에서 가격을 확인해 주세요.')"></el-alert>
        <el-alert
          v-else
          class="dm-hint dm-created__alert"
          type="info"
          :closable="false"
          show-icon
          :title="
            $t('배달앱에서 읽어 온 값으로 만들었습니다. 값을 고치려면 메뉴 수정 화면에서 바꿔 주세요.')
          "></el-alert>
      </div>
      <div slot="footer">
        <el-button size="small" type="primary" @click="createdVisible = false">{{ $t('확인') }}</el-button>
      </div>
    </el-dialog>

    <!--
      **일괄 결과도 유일한 확인 지점이다** (§16.183). 성공한 행은 목록에서 사라지므로, 무엇이
      확정됐고 무엇이 남았는지 여기서 말하지 않으면 사장님은 남은 것을 셀 방법이 없다.
      실패는 오류 알림이 아니라 **남은 할 일**로 보여 준다 — 나머지는 이미 확정됐기 때문이다.
    -->
    <el-dialog :title="$t('일괄 등록 결과')" :visible.sync="bulkResultVisible" width="620px">
      <div v-if="bulkResult">
        <el-alert
          class="dm-hint"
          :type="bulkResult.failed > 0 ? 'warning' : 'success'"
          :closable="false"
          show-icon
          :title="bulkResultTitle()"></el-alert>
        <!--
          **응답을 못 받은 요청이 있으면 위 숫자는 하한이다** (§16.183). 서버는 링크마다 트랜잭션을
          따로 닫으므로 타임아웃·5xx 때 이미 만들어진 건이 있을 수 있다 — 그 가능성을 말하지 않으면
          사장님이 같은 것을 다시 만들려 든다.
        -->
        <el-alert
          v-if="bulkResult.uncertain"
          class="dm-created__alert"
          type="warning"
          :closable="false"
          show-icon
          :title="
            $t('서버 응답을 받지 못한 요청이 있습니다. 일부는 이미 등록됐을 수 있으니 아래 목록을 확인해 주세요.')
          "></el-alert>
        <el-table v-if="bulkResult.failed > 0" :data="bulkFailures()" size="mini" border class="dm-created__alert">
          <el-table-column :label="$t('링크')" prop="linkId" width="90"></el-table-column>
          <el-table-column :label="$t('남은 이유')" show-overflow-tooltip>
            <template slot-scope="s">{{ s.row.message || $t('알 수 없는 오류') }}</template>
          </el-table-column>
        </el-table>
      </div>
      <div slot="footer">
        <el-button size="small" type="primary" @click="bulkResultVisible = false">{{ $t('확인') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import deliveryMenuScopeMixin from '@/mixins/deliveryMenuScopeMixin'
import { util } from '@/mixins/utilMixin'
import { initData } from '@/utils/constants'
import DeliveryMenuBindDialog from '@/components/deliveryMenu/DeliveryMenuBindDialog.vue'
import DeliveryMenuMaterializeDialog from '@/components/deliveryMenu/DeliveryMenuMaterializeDialog.vue'
import {
  DELIVERY_MENU_ACTIONS,
  DELIVERY_MENU_API,
  BULK_MATERIALIZE_MAX,
  canMaterializeRow,
  canPickStoreCategories,
  deliveryMenuErrorMessage,
  materializeCategoryBody,
  mergeBulkMaterializeResults,
  rejectedRequestResults,
  deliveryMenuPlatformName,
  deliveryMenuTimeText,
  hasObservedPrice,
  hasObservedOptions,
  observedOptionText,
  observedPriceText,
  shopText,
} from '@/utils/deliveryMenuSync'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'

/**
 * ③ ORPHAN 매칭 — `GET /orphans` · `POST /links/{id}/bind` · `POST /links/{id}/materialize`.
 *
 * ★★ **`AMBIGUOUS` 링크도 여기 온다** (§16.257). 조회의 대상은 `LinkState.NEEDS_BINDING` 이고
 * 그 집합은 `ORPHAN`(붙일 상대를 아직 못 정했다) + `AMBIGUOUS`(상대는 정해졌고 확정만 남았다)다.
 *
 * 확정 경로 자체는 ① 메뉴 목록 chip 에도 처음부터 있었다 — 없던 것은 **찾는 방법**이다. 사장님은
 * 「메뉴 연결 상태를 확인해 주세요」를 듣고 그 메뉴가 500개 중 어느 것인지 알 도리가 없었다.
 * 확정 UI 는 여전히 `DeliveryMenuBindDialog` 하나를 공유한다.
 *
 * **두 갈래를 가르는 축은 `link_state` 문자열이 아니라 `smenuCd` 다** — 창설(`materialize`)이 같은
 * 질문에 이미 그렇게 답한다. 술어는 `canMaterializeRow` 한 곳이 소유하고 단건 버튼·일괄
 * 체크박스가 함께 쓴다.
 *
 * **두 버튼의 뜻이 다르다** (§16.172).
 *  - `bind` — 오더엔에 이미 있는 메뉴 행에 확정한다.
 *  - `materialize` — 붙일 상대가 없을 때 **그 링크의 관측으로 메뉴 행을 만들고 그 자리에서 확정**한다.
 *    이 버튼이 없으면 요기요·쿠팡에만 있는 메뉴는 어느 버튼으로도 해소되지 않는 행으로 남는다.
 *
 * **`materialize` 본문에 실을 수 있는 것은 카테고리 하나뿐이고 그것도 선택이다** (§16.178).
 * 이름·가격은 전부 링크의 `observed_snapshot` 에서 온다 — 화면이 그 값을 실어 보내는 축을 열면
 * "플랫폼에 있는 것을 들인다"가 아니라 새 메뉴 등록이 되고, 그것은 메뉴 CRUD 가 답하는 질문이다.
 * 카테고리만 예외인 이유는 그 축이 **오더엔 쪽 사실**(POS 진열·오더톡 노출)이라 관측이 대신 답할 수
 * 없는 경우가 있고, 그때 사장님이 답할 자리가 없으면 그 행은 어느 버튼으로도 해소되지 않기 때문이다.
 *
 * `ORPHAN` 링크를 지우는 API 는 없다 — 붙이거나, 새로 만들거나, 그대로 두거나다.
 */
export default {
  name: 'DeliveryMenuOrphanView',
  mixins: [mainVueMixin, deliveryMenuScopeMixin],
  components: { DeliveryMenuBindDialog, DeliveryMenuMaterializeDialog },

  data() {
    const data = initData()
    data.group = false
    data.bindVisible = false
    data.bindLink = null
    data.bindStoreCd = null
    data.materializeVisible = false
    data.materializeLink = null
    /** 세션 매장의 보이는 카테고리 — `새 메뉴로 등록`의 "기존에서 선택" 갈래가 쓴다 (§16.178) */
    data.categories = []
    /** `materialize` 응답 — 만들어진 메뉴 행. 목록에서 그 행이 사라지므로 유일한 확인 지점이다 */
    data.createdVisible = false
    data.created = null
    /** 그 링크의 가격을 관측하지 못했는가 — 그러면 메뉴가 0원으로 만들어진다 (가이드 §4) */
    data.createdPriceUnknown = false
    /** 일괄 확정 대상 — 표의 체크박스가 소유한다 (§16.183) */
    data.selection = []
    data.bulkLoading = false
    /** 일괄 확정 응답 — 성공한 행은 목록에서 사라지므로 이 값이 유일한 확인 지점이다 */
    data.bulkResult = null
    data.bulkResultVisible = false
    /** 일괄의 카테고리 지정 대화상자 (§16.186) — 단건과 같은 자리를 준다 */
    data.bulkCategoryVisible = false
    data.bulkCategoryCds = []
    data.bulkCategoryNm = ''
    data.bulkCategoryCount = 0
    /** 이 요청에 실릴 링크가 전부 세션 매장 것인가 — 아니면 이름 갈래만 연다 (§16.186) */
    data.bulkCategoryPickable = false
    /** 대화상자의 답을 호출부의 await 로 돌려주는 자리 — 반응형일 필요가 없다 */
    data.bulkCategoryResolve = null
    return data
  },

  computed: {
    storeCd() {
      return this.scopeStoreCd
    },
    canBind() {
      return checkAuthActionAccess(DELIVERY_MENU_ACTIONS.BIND)
    },
    /**
     * 지금 고를 카테고리 목록이 **그 링크의 매장 것인가**.
     *
     * `GET /category/store` 에는 매장 축이 없어(세션 매장만 내린다) 형제 매장 링크에서는 고를 목록이
     * 없다. 남의 매장 카테고리를 보내면 서버가 400 이므로 그때는 이름 갈래만 연다 — 이름은 서버가
     * **그 링크의 매장에서** 확보하니 정확하다.
     */
    materializeCategoryPickable() {
      return canPickStoreCategories(this.materializeLink, this.storeCd)
    },
  },

  async created() {
    await this.loadScopeStore()
    this.search()
    this.loadCategories()
  },

  mounted() {
    this.$nextTick(() => {
      this.mainVue.currVue = this
    })
  },

  watch: {
    /**
     * 카테고리 대화상자가 **어떻게 닫히든** 기다리는 쪽을 풀어 준다 (§16.186).
     *
     * 확인 버튼만 풀어 주면 X·ESC·바깥 클릭으로 닫았을 때 호출부의 `await` 가 영영 매달리고,
     * 그 회차는 `bulkLoading` 없이 아무 일도 일어나지 않는 채로 끝난다. 확인은 닫기 **전에**
     * 값을 확정하므로(`confirmBulkCategory`) 여기 도달할 때는 이미 결과가 나가 있다.
     */
    bulkCategoryVisible(open) {
      if (!open) this.settleBulkCategory(null)
    },
  },

  methods: {
    platformName: deliveryMenuPlatformName,

    /** 샵 표기(이름 ?: 번호 + 부제) 규칙은 utils/deliveryMenuSync 한 곳이 소유한다 (§16.179) */
    shop: shopText,

    formatDate(value) {
      return deliveryMenuTimeText(value, util.getDate)
    },

    priceText(raw) {
      // 관측 원문은 문자열이고 숫자가 아닐 수 있다 — 표기 규칙은 utils/deliveryMenuSync 가 소유한다
      return observedPriceText(raw)
    },

    /**
     * 이 행이 `새 메뉴로 등록` 대상인가 (§16.257) — 버튼과 체크박스가 **같은 술어**를 쓴다.
     * 판정은 utils 한 곳이 소유한다.
     */
    canMaterialize: canMaterializeRow,

    /** 옵션 문구·판정도 utils 한 곳이 소유한다 (§16.195) — 목록·대화상자·결과가 같은 말을 한다 */
    optionText: observedOptionText,
    hasOptions(row) {
      return hasObservedOptions(row && row.observedOptionCount)
    },
    createdOptionText(created) {
      const text = observedOptionText(created && created.optionCount)
      const groups = created && created.optionGroupCount
      return groups ? `${text} (${this.$t('옵션그룹')} ${groups})` : text
    },

    async search() {
      if (this.storeCd == null) return
      try {
        const res = await this.$axios.get(`${DELIVERY_MENU_API}/orphans`, {
          params: { storeCd: this.storeCd, group: this.group },
        })
        this.tableData = Array.isArray(res.data) ? res.data : []
        // 매장명의 출처는 이 응답이다 — 이름을 얻으려 다른 API 를 부르지 않는다 (규약 §16.152)
        this.rememberStoreNames(this.tableData)
      } catch (e) {
        this.tableData = []
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e) })
      }
    },

    openBind(row) {
      this.bindLink = row
      // 링크의 매장으로 메뉴를 검색한다 — 그룹 조회면 형제 매장의 링크일 수 있다
      this.bindStoreCd = row.storeCd
      this.bindVisible = true
    },

    /**
     * 이 조작은 **오더엔에 메뉴 행을 만든다** — 되돌리는 API 가 없으므로 대화상자가 확인을 겸한다.
     * 그 안에서 카테고리를 정한다(§16.178) — 관측 이름이 기본값이고 고치거나 고를 수 있다.
     */
    openMaterialize(row) {
      this.materializeLink = row
      this.materializeVisible = true
    },

    onSelectionChange(rows) {
      this.selection = rows
    },

    /**
     * 선택한 링크를 한 번에 확정한다 (§16.183).
     *
     * **관측 카테고리가 있는 링크와 없는 링크를 두 요청으로 가른다** (§16.178·§16.183).
     * 카테고리를 실어 보내면 서버가 **요청 전체**에 적용해 관측이 알려 준 카테고리까지 덮으므로
     * 한 요청에 섞을 수 없고, 없는 쪽은 그냥 두면 건별 400 이라 사장님에게 이름을 받아야 한다.
     *
     * 200 이 "전부 성공"이 아니다 — 건별 결과가 배열로 오고, 실패한 건은 그 사유와 함께 남는다.
     */
    async openBulkMaterialize() {
      const rows = this.selection
      if (rows.length === 0) return
      // 서버 상한(`BULK_MATERIALIZE_MAX`)을 넘으면 요청 전체가 400 이라 **아무것도 등록되지 않는다**.
      // 이 화면에는 페이징이 없어 `형제 매장 포함` 을 켜면 그룹 전량이 한 표에 실리므로 현실적인
      // 경계다 — 보내기 전에 몇 건을 줄여야 하는지 말해 준다.
      if (rows.length > BULK_MATERIALIZE_MAX) {
        this.$message.warning(
          `${this.$t('한 번에')} ${BULK_MATERIALIZE_MAX}${this.$t('건까지 등록할 수 있습니다. 선택을 줄여 주세요.')}`
        )
        return
      }

      // **카테고리를 관측하지 못한 링크는 서버가 400 으로 거절한다** (§16.178) — 그 축은 오더엔
      // 쪽 사실이라 관측이 대신 답할 수 없기 때문이다. 단건 대화상자는 그때 입력을 강제하는데,
      // 일괄이 그 게이트를 우회하면 §16.183 이 겨냥한 바로 그 장면(새 매장 온보딩)에서 전량 실패한다
      // — dev 실측에서 ORPHAN 145건 중 143건이 그 400 이었다.
      //
      // 그래서 선택을 두 갈래로 나눈다. 카테고리를 실어 보내면 서버가 **요청 전체**에 적용하므로
      // (관측 카테고리를 가진 링크의 것까지 덮는다) 한 요청에 섞을 수 없다.
      const observed = rows.filter((row) => row.observedCategoryNm)
      const unknown = rows.filter((row) => !row.observedCategoryNm)
      // 단건과 **같은 자리**를 준다 (§16.186) — 기존 카테고리를 체크로 고르고, 원하면 새 이름도
      // 함께 준다. 이름만 받던 `$prompt` 는 이미 가진 카테고리를 다시 타이핑하게 했고 오타 하나면
      // 같은 뜻의 카테고리가 하나 더 생겼다.
      let unknownCategory = null
      if (unknown.length > 0) {
        unknownCategory = await this.askBulkCategory(unknown)
        if (unknownCategory === null) return // 사장님이 취소했다 — 오류가 아니다
      }

      // **문구 생성은 try 밖이다.** 안에서 평가하면 그 안의 어떤 오류든 아래 catch 가
      // "사장님이 취소했다"로 삼켜 조용히 return 한다 — 실제로 그렇게 기능이 통째로 죽었다.
      const confirmText = this.bulkConfirmText()
      try {
        await this.$confirm(confirmText, this.$t('확인'), { type: 'warning' })
      } catch (e) {
        return // 취소만 이 갈래로 온다
      }

      this.bulkLoading = true
      try {
        // 이 파일의 다른 호출과 **같은 클라이언트·같은 상수**다 — 경로를 리터럴로 적으면
        // `sys_resources` 가 인가하는 접두사(`/api/delivery-menu/**`)와 갈릴 수 있는 사본이 남는다.
        const requests = []
        if (observed.length > 0) {
          requests.push({ linkIds: observed.map((row) => row.linkId) })
        }
        if (unknown.length > 0) {
          requests.push({ linkIds: unknown.map((row) => row.linkId), ...unknownCategory })
        }
        // **요청마다 결과를 만든다 — 거절도 결과다.** 앞서 성공한 요청의 건별 결과는 이미 서버에
        // 반영된 사실이라, 뒤 요청이 거절됐다고 함께 버리면 사장님은 등록된 것을 알 수 없다.
        // `showErrorMessage: false` 로 인터셉터 토스트를 끈다 — 이 화면은 결과 대화상자로 답하므로
        // 토스트가 함께 뜨면 화면이 두 말을 한다(§16.175 의 옵트아웃 규칙: 대체 동작이 있을 때만).
        const responses = []
        for (const body of requests) {
          try {
            const res = await this.$axios.post(`${DELIVERY_MENU_API}/links/materialize`, body, {
              showErrorMessage: false,
            })
            responses.push(
              res && res.data ? res.data : rejectedRequestResults(body.linkIds, this.$t('서버 응답이 비어 있습니다.'))
            )
          } catch (e) {
            // 상태를 함께 넘긴다 — 400 이면 한 건도 안 들어갔지만 타임아웃·5xx 는 서버가 건별로
            // 커밋해 두고 응답만 못 준 것일 수 있다 (§16.183). 타임아웃은 `status` 자체가 없다.
            responses.push(rejectedRequestResults(body.linkIds, deliveryMenuErrorMessage(e), e && e.status))
          }
        }
        this.bulkResult = mergeBulkMaterializeResults(responses)
        this.bulkResultVisible = true
        // 목록은 언제나 갱신한다 — 등록된 행은 실제로 사라졌기 때문이다. 그리고 갱신 뒤에
        // **남은 것만 다시 선택해 둔다**: 선택을 비우면 사장님이 수십 건을 다시 골라야 하고
        // (§16.183), 그대로 두면 이미 등록돼 사라진 행까지 선택에 남는다.
        await this.search()
        this.reselectFailedRows()
      } finally {
        this.bulkLoading = false
      }
    },

    /**
     * 카테고리를 읽지 못한 건들을 어디에 넣을지 묻는다 (§16.186).
     *
     * 대화상자는 화면에 있고 답은 사장님이 누를 때 온다 — 그래서 `Promise` 로 감싸 호출부가
     * `await` 한 줄로 읽게 한다. 취소는 오류가 아니라 `null` 이다.
     */
    askBulkCategory(rows) {
      this.bulkCategoryCount = rows.length
      // 고를 수 있는지는 **이 요청에 실릴 링크들**이 정한다 — 카테고리 축은 요청 전체 공유라
      // (§16.183) 하나라도 다른 매장이면 그 목록을 쓸 수 없다.
      this.bulkCategoryPickable = canPickStoreCategories(rows, this.storeCd)
      this.bulkCategoryCds = []
      this.bulkCategoryNm = ''
      this.bulkCategoryVisible = true
      return new Promise((resolve) => {
        // 확인·취소·닫기 어느 쪽으로 끝나도 이 하나가 호출된다(아래 watch) — 대화상자가 열린 채
        // 잊히면 호출부의 await 가 영영 풀리지 않는다.
        this.bulkCategoryResolve = resolve
      })
    },

    /** 확인 — 고른 것과 새 이름의 합집합을 돌려준다(요청 본문 축은 utils 가 소유한다) */
    confirmBulkCategory() {
      const body = materializeCategoryBody({
        categoryCds: this.bulkCategoryCds,
        categoryNm: this.bulkCategoryNm,
      })
      this.bulkCategoryVisible = false
      this.settleBulkCategory(body)
    },

    /** 취소·닫기 — `null` 이 "고르지 않았다"이고 호출부는 그것을 취소로 읽는다 */
    settleBulkCategory(value) {
      const resolve = this.bulkCategoryResolve
      this.bulkCategoryResolve = null
      if (resolve) resolve(value)
    },

    bulkCategoryPrompt() {
      return `${this.bulkCategoryCount}${this.$t(
        '건은 배달앱 카테고리를 읽지 못했습니다. 그 메뉴들을 넣을 카테고리를 정해 주세요.'
      )}`
    },

    /**
     * 일괄 결과에서 **남은 링크만** 다시 선택한다 (§16.183 — 실패해도 선택을 비우지 않는다).
     *
     * 등록에 성공한 행은 갱신된 목록에 없으므로 자연히 빠지고, 남은 것만 체크된 채로 돌아온다.
     * 사장님이 사유를 고친 뒤 그대로 다시 누르면 된다.
     */
    reselectFailedRows() {
      const table = this.$refs.table
      if (!table) return
      const remaining = new Set(this.bulkFailures().map((r) => r.linkId))
      table.clearSelection()
      this.tableData.filter((row) => remaining.has(row.linkId)).forEach((row) => table.toggleRowSelection(row, true))
    },

    /**
     * 확인 문구 — **되돌릴 수 없는 쓰기라 무엇이 만들어지는지 먼저 말한다** (§16.183).
     *
     * 가격을 관측하지 못한 링크는 서버가 **0원**으로 만든다. 단건 등록은 그것을 사후 경고로 띄우는데,
     * 일괄에서 빠지면 38건 온보딩에서 0원 메뉴가 조용히 섞이고 그대로 POS·오더톡에서 팔린다.
     * 카테고리를 못 읽은 건은 앞서 받은 이름으로 들어가므로 그 사실도 함께 말한다.
     *
     * 치환자(`$t(key, params)`)를 쓰지 않는다 — 이 앱의 메시지는 서버 카탈로그에서만 오고
     * `formatFallbackMessages` 가 꺼져 있어 등록되지 않은 키는 `{n}` 이 글자 그대로 뜬다.
     */
    bulkConfirmText() {
      const rows = this.selection
      const head = `${this.$t('선택한')} ${rows.length}${this.$t('건을 배달앱 관측 그대로 새 메뉴로 등록합니다.')}`
      const zeroPriced = rows.filter((row) => !hasObservedPrice(row.observedPrice)).length
      const zero =
        zeroPriced > 0
          ? ` ${this.$t('이 중')} ${zeroPriced}${this.$t('건은 배달앱 가격을 읽지 못해 0원으로 만들어집니다.')}`
          : ''
      const noCategory = rows.filter((row) => !row.observedCategoryNm).length
      const category =
        noCategory > 0
          ? ` ${this.$t('이 중')} ${noCategory}${this.$t(
              '건은 배달앱 카테고리를 읽지 못해 입력하신 카테고리로 들어갑니다.'
            )}`
          : ''
      return `${head}${zero}${category} ${this.$t('계속할까요?')}`
    },

    /**
     * 만들어진 메뉴가 **어느 카테고리들에** 들어갔는가 (§16.186).
     *
     * 이 대화상자가 유일한 확인 지점이라(목록을 다시 읽으면 그 행은 사라진다) 대표 하나만 말하면
     * 나머지를 확인할 자리가 없다. `categories` 가 없는 응답(옛 서버)은 대표 축으로 떨어진다.
     */
    createdCategoryText(created) {
      if (!created) return '-'
      const names = (created.categories || []).map((c) => c.categoryNm).filter(Boolean)
      return names.length > 0 ? names.join(', ') : created.categoryNm || '-'
    },

    /** 결과 한 줄 — 등록된 수와 남은 수를 함께 말한다(치환자를 쓰지 않는 이유는 위와 같다) */
    bulkResultTitle() {
      const r = this.bulkResult
      if (!r) return ''
      const ok = `${r.succeeded}${this.$t('건 등록했습니다.')}`
      return r.failed > 0 ? `${ok} ${r.failed}${this.$t('건이 남았습니다.')}` : ok
    },

    /** 일괄 결과에서 실패한 건만 — 성공은 목록에서 사라지므로 남는 것이 곧 할 일이다 */
    bulkFailures() {
      const results = (this.bulkResult && this.bulkResult.results) || []
      return results.filter((r) => !r.ok)
    },

    /** 만들어진 메뉴는 **이 응답으로만** 확인할 수 있다 — 목록을 다시 읽으면 그 행은 사라진다 */
    onMaterialized({ link, created }) {
      this.created = created
      this.createdPriceUnknown = !hasObservedPrice(link && link.observedPrice)
      this.createdVisible = true
      this.search()
    },

    /**
     * `새 메뉴로 등록`의 "기존 카테고리에서 선택" 갈래가 쓸 목록 (§16.178).
     *
     * 메뉴 관리 화면과 같은 경로(`GET /category/store`)라 사장님 계정에 열려 있고, 매장 축이 없어
     * **세션 매장만** 내린다 — 형제 매장 링크에서 이 목록을 쓰지 않는 이유다.
     */
    async loadCategories() {
      try {
        const res = await this.$axios.get('/category/store', {
          params: { categoryNm: '', useYn: 'Y' },
          showLoading: false,
        })
        this.categories = Array.isArray(res.data) ? res.data : []
      } catch (e) {
        // 문장은 인터셉터가 이미 띄웠다 — 여기서 또 말하면 화면이 두 말을 한다(§16.175 와 같은 이유).
        // 목록이 비어도 `새 메뉴로 등록`은 이름 갈래로 그대로 동작한다.
        this.categories = []
      }
    },
  },
}
</script>

<style scoped>
.dm-scope {
  font-weight: 600;
}
.dm-hint {
  margin-bottom: 8px;
}
.dm-weak {
  color: #909399;
}
.dm-candidate {
  margin: 1px 4px 1px 0;
}
.dm-bulk__sublabel {
  margin: 12px 0 6px;
  font-size: 12px;
  color: #606266;
}
/* 카테고리가 많은 매장에서 대화상자가 끝없이 길어지지 않게 한다 — 목록은 자기 안에서 스크롤한다 */
.dm-bulk__checks {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 180px;
  overflow-y: auto;
}
.dm-bulk__checks .el-checkbox {
  margin: 0;
}
.dm-created__alert {
  margin-top: 12px;
  margin-bottom: 0;
}
</style>
